<script setup lang="ts">
import AnnotatedEventDO = App.Data.AnnotatedEventDO;
import { onMounted, ref, watch } from "vue";
import { useApiClient } from "@/api/client";
import { loadingWrapper } from "@/api/helpers/loadingWrapper";
import dayjs from "dayjs";
import { toastApiResult } from "@/toaster";
import DateTextField from "@/modules/annotations/components/DateTextField.vue";
import UpsertAnnotatedEventDTO = App.Data.UpsertAnnotatedEventDTO;
import ModuleLoading from "@/ui/components/loading/moduleLoading.vue";
import AnnotatedEventCategoryDO = App.Data.AnnotatedEventCategoryDO;
import { useForm } from "laravel-precognition-vue";
import Switch from "@/ui/components/switch/switch.vue";
import { titleInputRules } from "@/ui/validation/validationRules";
import { t } from "@/plugins/i18n";
import useDialog from "@/ui/composables/useDialog";

const createEmptyAnnotatedEvent = (): UpsertAnnotatedEventDTO => {
	return {
		id: null,
		title: "",
		description: "",
		from: new Date(),
		to: new Date(),
		applies_to_measure_date: false,
		applies_to_subject_date: true,
		category_id: null,
	};
};

const annotatedEvents = ref([] as AnnotatedEventDO[]);
const categories = ref([] as AnnotatedEventCategoryDO[]);
const showAddAnnotatedEvent = ref(false);
const form = useForm(
	() => (form.id ? "patch" : "post"),
	() => (form.id ? `/annotated-events/${form.id}` : "/annotated-events"),
	createEmptyAnnotatedEvent()
);
form.validateFiles(); // validateFiles is necessary to make the dates work. Without it, it tries to remove files from the input and it triggers for dates.

const submit = () => {
	form.submit({
		async onSuccess(response) {
			showAddAnnotatedEvent.value = false;
			form.setData(createEmptyAnnotatedEvent());
			form.reset();

			await loadAnnotatedEvents();
		},
	});
};

const tableHeaders = [
	{ title: "Category", key: "category" },
	{ title: "Title", key: "title" },
	{ title: "From", key: "from" },
	{ title: "To", key: "to" },
	{ title: "Parking Date", key: "applies_to_subject_date" },
	{ title: "Snapshot Date", key: "applies_to_measure_date" },
	{ title: "Actions", key: "actions", align: "end" },
];

const loadAnnotatedEvents = async () => {
	const response = await loadingWrapper(useApiClient("annotatedEvents").get());

	if (response.isOk()) {
		annotatedEvents.value = response.value;
	}
};

const loadCategories = async () => {
	const response = await useApiClient("annotatedEventCategories").get();

	if (response.isOk()) {
		categories.value = response.value;
	}
};

const editAnnotation = (item: AnnotatedEventDO) => {
	form.setData({
		id: item.id,
		from: dayjs(item.from).toDate(),
		to: dayjs(item.to).toDate(),
		applies_to_subject_date: item.applies_to_subject_date,
		applies_to_measure_date: item.applies_to_measure_date,
		category_id: item.category?.id,
		title: item.title,
		description: item.description,
	});

	showAddAnnotatedEvent.value = true;
};

const deleteAnnotation = async (annotatedEvent: AnnotatedEventDO) => {
	const confirm = await useDialog().confirm({
		title: "Delete Annotation",
		message: `Are you sure you want to delete: "${annotatedEvent.title}"?`,
		isDelete: true,
	});

	if (!confirm) return;

	await toastApiResult(
		useApiClient("annotatedEvents").delete(annotatedEvent.id)
	);

	await loadAnnotatedEvents();
};

const formatDate = (str) => dayjs(str).format("YYYY-MM-DD");

onMounted(() => {
	loadAnnotatedEvents();
	loadCategories();
});

watch(showAddAnnotatedEvent, (v) => {
	if (!v) {
		form.setData(createEmptyAnnotatedEvent());
	}
});
</script>

<template>
	<v-dialog v-model="showAddAnnotatedEvent" width="600">
		<v-card>
			<v-toolbar elevation="0" flat>
				<v-toolbar-title>
					<i class="fa-regular fa-comment fa-xl mr-3"></i>
					<span v-if="form.id">Update</span><span v-else>Add</span> Annotation
				</v-toolbar-title>
				<v-spacer />

				<v-btn icon="true" @click="showAddAnnotatedEvent = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>

			<v-card-text>
				<v-form>
					<div class="py-2">
						<label class="text-h6">From:</label>
					</div>
					<date-text-field
						hide-details="auto"
						variant="filled"
						v-model="form.from"
						@update:modelValue="form.validate('from')"
						:error-messages="form.errors.from"
						data-testid="annotation-date-from"
					/>

					<div class="py-2">
						<label class="text-h6">To:</label>
					</div>
					<date-text-field
						hide-details="auto"
						variant="filled"
						v-model="form.to"
						@update:modelValue="form.validate('to')"
						:error-messages="form.errors.to"
						data-testid="annotation-date-to"
					/>

					<v-row class="pt-4 pb-4">
						<v-col>
							<Switch
								hide-details="auto"
								label="Applies to parking date"
								label-position="right"
								v-model="form.applies_to_subject_date"
								@update:model-value="form.validate('applies_to_subject_date')"
								:error-messages="form.errors.applies_to_subject_date"
							/>
						</v-col>
						<v-col>
							<Switch
								hide-details="auto"
								label="Applies to snapshot date"
								label-position="right"
								v-model="form.applies_to_measure_date"
								@update:model-value="form.validate('applies_to_measure_date')"
								:error-messages="form.errors.applies_to_measure_date"
							/>
						</v-col>
					</v-row>

					<div class="pb-2">
						<label class="text-h6">Category:</label>
					</div>
					<v-select
						hide-details="auto"
						variant="filled"
						clearable
						v-model="form.category_id"
						:items="categories"
						item-title="name"
						item-value="id"
						@update:modelValue="form.validate('category_id')"
						:error-messages="form.errors.category_id"
						data-testid="annotation-category"
					/>

					<div class="py-2">
						<label class="text-h6">Title:</label>
					</div>
					<v-text-field
						hide-details="auto"
						v-model="form.title"
						@update:modelValue="form.validate('title')"
						:error-messages="form.errors.title"
						:rules="titleInputRules(t)"
						data-testid="annotation-title"
					/>

					<div class="py-2">
						<label class="text-h6">Description (optional):</label>
					</div>
					<v-textarea
						hide-details="auto"
						variant="filled"
						v-model="form.description"
						@update:modelValue="form.validate('description')"
						:error-messages="form.errors.description"
					/>
				</v-form>
			</v-card-text>
			<v-divider />
			<v-card-actions>
				<v-spacer />
				<v-btn
					variant="flat"
					:disabled="form.hasErrors || form.processing"
					@click="submit()"
					>{{ form.id ? "Update" : "Create" }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>

	<v-card class="pa-0 mt-5" elevation="4">
		<div class="d-flex pa-4">
			<v-spacer />
			<v-btn
				variant="flat"
				color="primary"
				@click="showAddAnnotatedEvent = true"
			>
				<i class="fa-duotone fa-plus fa-lg mr-3"></i> Add new annotation
			</v-btn>
		</div>

		<module-loading>
			<div class="px-4">
				<v-data-table :headers="tableHeaders" :items="annotatedEvents">
					<template v-slot:item.category="{ value }">
						<v-chip
							label
							v-if="value"
							:color="value.rgb_color_value"
							:key="value.icon"
							:prepend-icon="`fa: ${value.icon}`"
							>{{ value.name }}
						</v-chip>
						<v-icon v-else>mdi-minus</v-icon>
					</template>

					<template v-slot:item.title="{ item }">
						<v-tooltip :text="item.description" location="right">
							<template v-slot:activator="{ props }">
								<span v-bind="props">{{ item.title }}</span>
							</template>
						</v-tooltip>
					</template>

					<template v-slot:item.from="{ value }"
						>{{ formatDate(value) }}
					</template>
					<template v-slot:item.to="{ value }">{{
						formatDate(value)
					}}</template>

					<template v-slot:item.applies_to_subject_date="{ value }">
						<v-checkbox-btn
							readonly
							color="primary"
							hide-details
							:model-value="value"
						/>
					</template>

					<template v-slot:item.applies_to_measure_date="{ value }">
						<v-checkbox-btn
							readonly
							color="primary"
							hide-details
							:model-value="value"
						/>
					</template>

					<template v-slot:item.actions="{ item }">
						<v-btn
							size="32"
							icon
							variant="text"
							@click="editAnnotation(item)"
							color="on-surface"
						>
							<i class="fa-light fa-pencil"></i
						></v-btn>
						<v-btn
							size="32"
							icon
							color="error"
							variant="text"
							@click="deleteAnnotation(item)"
							data-testid="delete-annotation"
						>
							<i class="fa-light fa-trash-can text-error"></i
						></v-btn>
					</template>
				</v-data-table>
			</div>
		</module-loading>
	</v-card>
</template>

<style scoped lang="scss"></style>
